import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { AutomaticEmailDistributionModal } from "../shared";
import { DateRangePicker } from "@/components/common/DateRangePicker";
import { Button } from "@/components/ui/button";
import type { DateRange } from "@/hooks/useDateRange";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { Role } from "@/types/user";
import { useSelectedHotelId } from "@/utils/hotelContext";
import { useAnalyticsTabExport } from "../../context/AnalyticsTabExportContext";
import { getAnalyticsMaxSelectableDate } from "../../utils/analyticsDateUtils";
import { AnalyticsExportMenu } from "../shared/AnalyticsExportMenu";
import { ANALYTICS_TOOLBAR_BUTTON_CLASS } from "../shared/toolbarButtonStyles";
import type { AnalyticsReportTab } from "../shared/email-distribution/autoExportMappers";

interface OverviewFiltersBarProps {
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  defaultTab?: string;
  availableReportTabs?: ReadonlyArray<AnalyticsReportTab>;
}

export const OverviewFiltersBar: React.FC<OverviewFiltersBarProps> = ({
  dateRange,
  onDateRangeChange,
  defaultTab,
  availableReportTabs,
}) => {
  const { t } = useTranslation();
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const { registration } = useAnalyticsTabExport();
  const { userProfile } = useAuthenticatedUser();
  const { hotelId: routeHotelId } = useParams<{ hotelId?: string }>();
  const selectedHotelId = useSelectedHotelId();
  const [searchParams] = useSearchParams();
  const hotelId = routeHotelId ?? selectedHotelId;
  const isTenantAdmin =
    userProfile?.role === Role.TENANT_ADMIN ||
    userProfile?.role === "SUPER_ADMIN";
  const activeTab = defaultTab ?? searchParams.get("tab") ?? "overview";

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white py-2">
      <div className="flex flex-wrap items-center gap-2">
        <DateRangePicker
          dateRange={dateRange}
          onDateRangeChange={onDateRangeChange}
          maxDate={getAnalyticsMaxSelectableDate()}
          className="border-border min-h-8 rounded-md border bg-white px-3 py-1.5 text-sm"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="span"
          className={ANALYTICS_TOOLBAR_BUTTON_CLASS}
          onClick={() => setIsEmailModalOpen(true)}
        >
          {t(
            "analytics.overview.automaticEmailDistribution",
            "Automatic email distribution",
          )}
        </Button>
        <AutomaticEmailDistributionModal
          open={isEmailModalOpen}
          onOpenChange={setIsEmailModalOpen}
          hotelId={hotelId}
          isTenantAdmin={isTenantAdmin}
          defaultTab={activeTab}
          availableReportTabs={availableReportTabs}
        />
        <AnalyticsExportMenu
          onExport={(format) => {
            registration?.onExport(format)?.catch(() => undefined);
          }}
          disabled={!registration?.canExport}
          formats={registration?.formats}
          label={t("analytics.common.export", "Export")}
          className={ANALYTICS_TOOLBAR_BUTTON_CLASS}
        />
      </div>
    </div>
  );
};
